/**
 * Team avatars — each member's primary stack mark on a steel tile, drawn as
 * inline SVG.
 *
 * The frame (backdrop, contour rings, halo) comes from the CSS custom
 * properties declared in styles/sections/team.css, so the tiles sit in the
 * White & Steel theme the way the rest of the section does. The marks
 * themselves are the shared brand logos in TechLogos.js.
 */

import { techLogos } from "./TechLogos.js";

const FALLBACK_STACK = "react";
const TONE_COUNT = 3;

/* Portrait fills a 3:4 card; square centres on the mark for small tiles. */
const VIEWBOX = {
  portrait: "0 0 300 400",
  square: "0 30 300 300",
};

/* The mark sits centred at (150, 180) in the portrait frame. */
const MARK = { x: 78, y: 108, size: 144 };

function slugify(value) {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function avatarId(member, index, scope) {
  return `ta-${scope}-${slugify(member.name) || index}`;
}

function toneOf(index) {
  return (index % TONE_COUNT) + 1;
}

/** The drawing itself, in raw 300×400 coordinates — the crop is the caller's job. */
function artwork(member, uid) {
  const logo = techLogos[member.stack] || techLogos[FALLBACK_STACK];
  const mark = logo.svg.replace(
    "<svg ",
    `<svg x="${MARK.x}" y="${MARK.y}" width="${MARK.size}" height="${MARK.size}" `
  );

  return `
      <defs>
        <linearGradient id="${uid}-bg" x1="0" y1="0" x2="0.55" y2="1">
          <stop offset="0" style="stop-color: var(--ta-bg-0, #ffffff)" />
          <stop offset="1" style="stop-color: var(--ta-bg-1, #c5ced6)" />
        </linearGradient>
        <radialGradient id="${uid}-halo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stop-color="${logo.accent}" stop-opacity="0.32" />
          <stop offset="1" stop-color="${logo.accent}" stop-opacity="0" />
        </radialGradient>
      </defs>

      <rect width="300" height="400" fill="url(#${uid}-bg)" />

      <g fill="none" stroke="var(--ta-line, rgba(28, 36, 44, 0.1))" stroke-width="1.1">
        <circle cx="34" cy="24" r="68" />
        <circle cx="34" cy="24" r="116" />
        <circle cx="34" cy="24" r="172" />
        <circle cx="272" cy="374" r="62" />
        <circle cx="272" cy="374" r="112" />
      </g>

      <circle cx="150" cy="180" r="140" fill="url(#${uid}-halo)" />
      <circle cx="150" cy="180" r="112" fill="none" stroke="var(--ta-ring, rgba(28, 36, 44, 0.14))" stroke-width="1.25" />

      <g class="team-avatar__figure">${mark}</g>
  `;
}

/**
 * One hidden definition per member, so a row that repeats the members dozens of
 * times draws two nodes per tile instead of the whole tile. Each definition
 * carries its own tone, which is what the gradients inside it inherit.
 *
 * @param {object[]} members  content.team.members.
 * @param {string} scope      Namespaces the ids for this row.
 */
export function buildTeamAvatarSprite(members, scope = "a") {
  const defs = members
    .map((member, index) => {
      const uid = avatarId(member, index, scope);
      return `<g id="${uid}-art" class="team-avatar" data-tone="${toneOf(index)}">${artwork(member, uid)}</g>`;
    })
    .join("");

  return `<svg class="team-avatar-sprite" aria-hidden="true" focusable="false"><defs>${defs}</defs></svg>`;
}

/** "8+ years experience" — the badge shown under every mark. */
export function experienceLabel(member) {
  return `${member.experience}+ years experience`;
}

/** An avatar drawn from the sprite above. Needs that sprite in the same document. */
export function buildTeamAvatarRef(member, index = 0, scope = "a", crop = "portrait") {
  return `
    <svg
      class="team-avatar"
      data-tone="${toneOf(index)}"
      data-crop="${crop}"
      viewBox="${VIEWBOX[crop] || VIEWBOX.portrait}"
      aria-hidden="true"
      focusable="false"
    ><use href="#${avatarId(member, index, scope)}-art" /></svg>
  `;
}
